import { pendingTask, begin, end } from '../constants/pendingTask';
import * as actions from './actionTypes';
import { fetchFromReitApi as fetch } from './fetchFromApi';
import * as actionHelpers from '../scripts/actionHelpers';
import { download } from '../scripts/fileHelpers';
/**
 * Fetch all Securities Snapshot records belonging to the reit period. If successful this will dispatch the LOAD_SECURITIES_SNAPSHOTS_SUCCESS
 * action, otherwise it will dispatch the LOAD_SECURITIES_SNAPSHOTS_FAILURE action.
 * @param {number} periodId The id of the period that owns the snapshot.
 * @returns {funtion} A function that returns a Promise.
 */
export function fetchSecuritiesSnapshotByPeriod(
  periodId,
  displayZeroBalanceSystemGeneratedRows
) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    const displayZeroBalanceRowsParam =
      displayZeroBalanceSystemGeneratedRows == undefined
        ? ''
        : displayZeroBalanceSystemGeneratedRows
          ? '&displayZeroBalanceSystemGeneratedRows=true'
          : '&displayZeroBalanceSystemGeneratedRows=false';

    return fetch(
      `/securities?reportPeriodId=${periodId}${displayZeroBalanceRowsParam}`
    )
      .then((response) => {
        return response.json();
      })
      .then((securitiesSnapshots) => {
        if (actionHelpers.isErrorResponse(securitiesSnapshots)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_SECURITIES_SNAPSHOTS_FAILURE,
            securitiesSnapshots
          );
        }

        dispatch({
          type: actions.LOAD_SECURITIES_SNAPSHOTS_SUCCESS,
          securitiesSnapshots,
          [pendingTask]: end,
        });
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_SECURITIES_SNAPSHOTS_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Updates a securities snapshot to the REIT API database.
 * @param {any} securitiesSnapshot The securities snapshot
 * @returns {Promise} A Promise
 * http://localhost:5000/api/securities/3
 */
export function updateSecuritiesSnapshot(securitiesSnapshot) {
  const period = securitiesSnapshot.reportPeriodID;
  const payload = {
    securitiesSnapshotID: securitiesSnapshot.securitiesSnapshotID,
    allSharesFromIssuerValue: securitiesSnapshot.allSharesFromIssuerValue,
    class: securitiesSnapshot.class,
    issuer: securitiesSnapshot.issuer,
    securityName: securitiesSnapshot.securityName,
    reitOwnedSharesValue: securitiesSnapshot.reitOwnedSharesValue,
    percentOfVote: securitiesSnapshot.percentOfVote,
    isTenPercentValueTestDisabled:
      securitiesSnapshot.isTenPercentValueTestDisabled,
    isSystemGenerated: securitiesSnapshot.isSystemGenerated,
  };

  return fetch(`/securities/${securitiesSnapshot.securitiesSnapshotID}`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'PUT',
    body: JSON.stringify(payload),
  }).then((response) => {
    if (response.ok) {
      return null;
    }

    return response.json();
  });
}

/**
 * get Incomplete Security Snapshot records belonging to the reit and report period. If successful this will dispatch the GET_INCOMPLETE_SECURITIES_SNAPSHOTS_SUCCESS
 * action, otherwise it will dispatch the GET_INCOMPLETE_SECURITIES_SNAPSHOTS_FAILURE action.
 * @param {number} clientID
 * @param {number} reitID
 * @param {number} reportPeriodID
 * @returns {function} A function that returns a Promise.
 */
export function getIncompleteSecuritySnapshotDetails(
  clientID,
  reitID,
  reportPeriodID,
  pageName = null
) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    return fetch(
      `/securities/getIncompleteSecuritySnapshotsDetail?clientID=${clientID}&reitID=${reitID}&reportPeriodID=${reportPeriodID}&pageName=${pageName}`
    )
      .then((response) => {
        return response.json();
      })
      .then((incompleteSecuritiesSnapshots) => {
        if (actionHelpers.isErrorResponse(incompleteSecuritiesSnapshots)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.GET_INCOMPLETE_SECURITIES_SNAPSHOTS_FAILURE,
            null
          );
        }
        dispatch({
          type: actions.GET_INCOMPLETE_SECURITIES_SNAPSHOTS_SUCCESS,
          incompleteSecuritiesSnapshots,
          [pendingTask]: end,
        });
        return incompleteSecuritiesSnapshots;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.GET_INCOMPLETE_SECURITIES_SNAPSHOTS_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * get Incomplete Security Snapshot records belonging to the reit and report period (WITHOUT DISPATCH).
 * @param {number} clientID
 * @param {number} reitID
 * @param {number} reportPeriodID
 * @returns {function} A function that returns a Promise.
 */
export function getIncompleteSecuritySnapshotDetailsWithoutDispatch(
  clientID,
  reitID,
  reportPeriodID,
  pageName = null
) {
  return fetch(
    `/securities/getIncompleteSecuritySnapshotsDetail?clientID=${clientID}&reitID=${reitID}&reportPeriodID=${reportPeriodID}&pageName=${pageName}`
  ).then((response) => {
    return response.json();
  });
}

export function uploadQuarterlySecurityTemplate(
  clientId,
  reportPeriodId,
  file
) {
  let formData = new FormData();
  formData.append('clientId', clientId);
  formData.append('reportPeriodId', reportPeriodId);
  formData.append('file', file);

  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/Securities/importQuarterlySecurities', {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (actionHelpers.isErrorResponse(response)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_QUARTERLY_SECURITY_FAILURE,
            response
          );
        }
        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPLOAD_QUARTERLY_SECURITY_FAILURE,
          null,
          error
        );
      });
  };
}

export function exportQuarterlySecuritiesErrorFile(
  clientId,
  periodId,
  fileUploadId
) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/Securities/downloadQuarterlySecurityErrorReport?clientId=${clientId}&reportPeriodId=${periodId}&fileUploadId=${fileUploadId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename); // Assuming 'download' is a globally available function or imported
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

export function exportQuarterlySecuritiesData(clientId, periodId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/Securities/exportQuarterlySecuritiesData?clientId=${clientId}&periodId=${periodId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}
